import { z } from "zod";

// Film status types
export type FilmStatus = "fresh" | "exposed" | "expired" | "developed";
export type DatabaseFilmStatus = "fresh" | "exposed" | "expired" | "developed";

// Film types
export type FilmType = "35mm" | "120" | "220" | "large_format" | "instant_film";

// Database Film types - must match exactly what Supabase expects
export type DatabaseFilmType = "35mm" | "120" | "220" | "large_format" | "instant_film";

// Film schema and types
export const filmSchema = z.object({
  film_name: z.string().min(1, { message: "Film name is required" }),
  film_type: z.enum(["35mm", "120", "220", "large_format", "instant_film"]),
  status: z.enum(["fresh", "exposed", "expired", "developed"]), // Updated to remove "unexposed"
  expiration_date: z.string().optional().nullable(),
  iso: z.number().min(1, { message: "ISO must be at least 1" }),
  brand: z.string().min(1, { message: "Brand is required" }),
  quantity: z.number().min(1, { message: "Quantity must be at least 1" }),
  notes: z.string().optional().nullable(),
});

export type FilmFormValues = z.infer<typeof filmSchema>;

export interface FilmDatabaseFields {
  film_name: string;
  film_type: DatabaseFilmType;
  status: DatabaseFilmStatus;
  expiration_date: string | null;
  iso: number;
  brand: string;
  quantity: number;
  notes: string | null;
  user_id: string;
}

export interface FilmInventory {
  id?: string;
  film_name: string;
  film_type: FilmType;
  status: FilmStatus;
  expiration_date: string | null;
  iso: number;
  brand: string;
  quantity: number;
  notes: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}

// Status mapping functions are now identity functions since we're aligning the types
export function mapStatusToDatabase(status: FilmStatus): DatabaseFilmStatus {
  return status; // No conversion needed anymore
}

// Status mapping functions are now identity functions since we're aligning the types
export function mapStatusFromDatabase(status: DatabaseFilmStatus): FilmStatus {
  return status; // No conversion needed anymore
}

// Camera schema and types
export const cameraSchema = z.object({
  brand: z.string().min(1, { message: "Brand is required" }),
  model: z.string().min(1, { message: "Model is required" }),
  camera_type: z.string().min(1, { message: "Camera type is required" }),
  format: z.string().nullable(),
  mount: z.string().nullable(),
  status: z.enum(["working", "needs_repair", "for_parts"]),
  serial_number: z.string().optional().nullable(),
  purchase_date: z.string().optional().nullable(),
  purchase_price: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  adapter_enabled: z.boolean().optional(),
  adapter_mounts: z.string().optional().nullable(),
});

export interface CameraFormValues {
  id?: string;
  brand: string;
  model: string;
  camera_type: string;
  format: string | null;
  mount: string | null;
  status: string;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_price: string | null;
  notes: string | null;
  adapter_enabled?: boolean;
  adapter_mounts?: string | null;
}

export interface CameraInventory {
  id: string;
  brand: string;
  model: string;
  camera_type: string;
  format: string | null;
  mount: string | null;
  status: string;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_price: number | null;
  notes: string | null;
  user_id: string;
  created_at: string;
  adapter_enabled: boolean | null;
  adapter_mounts: string | null;
  updated_at?: string;
}

export const assertCameraInventory = (data: any): asserts data is CameraInventory[] => {
  if (!Array.isArray(data)) {
    throw new Error('Data is not an array');
  }

  data.forEach((item, index) => {
    if (typeof item !== 'object' || item === null) {
      throw new Error(`Item at index ${index} is not an object`);
    }

    const requiredKeys: (keyof CameraInventory)[] = [
      'id', 'brand', 'model', 'camera_type', 'status', 'user_id', 'created_at'
    ];

    requiredKeys.forEach(key => {
      if (!(key in item)) {
        throw new Error(`Key "${key}" is missing in item at index ${index}`);
      }
    });
  });
};

// Lens schema and types
export const lensSchema = z.object({
  brand: z.string().min(1, { message: "Brand is required" }),
  lens_name: z.string().min(1, { message: "Lens name is required" }),
  mount: z.string().min(1, { message: "Mount is required" }),
  status: z.enum(["working", "needs_repair", "for_parts"]),
  serial_number: z.string().optional().nullable(),
  purchase_date: z.string().optional().nullable(),
  purchase_price: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type LensFormValues = z.infer<typeof lensSchema>;

export type LensStatus = "working" | "needs_repair" | "for_parts";

export interface LensInventory {
  id: string;
  brand: string;
  lens_name: string;
  mount: string;
  status: LensStatus;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_price: number | null;
  notes: string | null;
  user_id: string;
  created_at: string;
  updated_at?: string;
}

export const assertLensInventory = (data: any): asserts data is LensInventory[] => {
  if (!Array.isArray(data)) {
    throw new Error('Data is not an array');
  }

  data.forEach((item, index) => {
    if (typeof item !== 'object' || item === null) {
      throw new Error(`Item at index ${index} is not an object`);
    }

    const requiredKeys: (keyof LensInventory)[] = [
      'id', 'brand', 'lens_name', 'mount', 'status', 'user_id', 'created_at'
    ];

    requiredKeys.forEach(key => {
      if (!(key in item)) {
        throw new Error(`Key "${key}" is missing in item at index ${index}`);
      }
    });
  });
};

export type InventoryCategory = 'film' | 'camera' | 'lens';

// Empty SQL definitions to satisfy imports
export const cameraTableSQL = "";
export const lensTableSQL = "";
